import React from "react";
import PropTypes from "prop-types";
import { Spin } from "antd";
import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";
import GenericCard from "components/UI/GenericCard/GenericCard";
import DashedTooltip from "components/UI/DashedTooltip";
import { returnUpDownClassname } from "utils/helpers";

// Robustness verdict for the current backtest -- data from
// POST /predictions/backtest-health (dashboard_backend backtest-health.js).

const VERDICTS = {
  robust: {
    label: "Robust",
    color: "#22c55e",
    text: "Holds up out of sample, across years and nearby parameters.",
  },
  caution: {
    label: "Caution",
    color: "#eab308",
    text: "Some checks are borderline -- trade it small or watch it live first.",
  },
  weak: {
    label: "Weak",
    color: "#ef4444",
    text: "At least one check failed -- likely overfit or not enough evidence.",
  },
  insufficient: {
    label: "Not enough data",
    color: "#9ca3af",
    text: "Too few bars in the range to judge.",
  },
};

const STATUS_ICONS = {
  pass: <CheckCircleFilled style={{ color: "#22c55e" }} />,
  warn: <ExclamationCircleFilled style={{ color: "#eab308" }} />,
  fail: <CloseCircleFilled style={{ color: "#ef4444" }} />,
};

const fmt = (value, suffix = "") =>
  value == null || !Number.isFinite(+value)
    ? "--"
    : `${(+value).toLocaleString(undefined, { maximumFractionDigits: 2 })}${suffix}`;

const Pct = ({ value }) => (
  <span className={returnUpDownClassname(value)}>{fmt(value, "%")}</span>
);
Pct.propTypes = { value: PropTypes.number };

const cellStyle = { padding: "4px 10px", textAlign: "right" };
const headStyle = { ...cellStyle, opacity: 0.6, fontWeight: 400 };

const HoldoutTable = ({ holdout }) => {
  const rows = [
    ["CAGR", "cagr", "%"],
    ["Max drawdown", "maxDrawdown", "%"],
    ["Calmar", "calmarRatio"],
    ["Sharpe", "sharpeRatio"],
    ["Win rate", "successRate", "%"],
    ["Trades", "trades"],
  ];
  return (
    <div>
      <h5 className="mb-2">
        <DashedTooltip text="The range is split at 70% of its bars. Both parts run separately from the initial balance. A robust strategy keeps most of its Calmar in the later part.">
          Holdout (split {holdout.splitDate})
        </DashedTooltip>
      </h5>
      <table>
        <thead>
          <tr>
            <th style={headStyle} />
            <th style={headStyle}>First 70%</th>
            <th style={headStyle}>Last 30%</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([label, key, suffix]) => (
            <tr key={key}>
              <td style={{ ...cellStyle, textAlign: "left" }}>{label}</td>
              <td style={cellStyle}>{fmt(holdout.inSample[key], suffix)}</td>
              <td style={cellStyle}>{fmt(holdout.outOfSample[key], suffix)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
HoldoutTable.propTypes = { holdout: PropTypes.object };

const YearsTable = ({ years }) => (
  <div>
    <h5 className="mb-2">
      <DashedTooltip text="Each calendar year run on its own from the initial balance. * = partial year.">
        Year by year
      </DashedTooltip>
    </h5>
    <table>
      <thead>
        <tr>
          <th style={headStyle}>Year</th>
          <th style={headStyle}>Return</th>
          <th style={headStyle}>Max DD</th>
          <th style={headStyle}>Trades</th>
          <th style={headStyle}>Buy & hold</th>
        </tr>
      </thead>
      <tbody>
        {years.map((y) => (
          <tr key={y.year}>
            <td style={{ ...cellStyle, textAlign: "left" }}>
              {y.year}
              {y.partial ? "*" : ""}
            </td>
            <td style={cellStyle}>
              <Pct value={y.returnPercent} />
            </td>
            <td style={cellStyle}>{fmt(y.maxDrawdown, "%")}</td>
            <td style={cellStyle}>{y.trades}</td>
            <td style={cellStyle}>
              <Pct value={y.buyHoldReturn} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
YearsTable.propTypes = { years: PropTypes.array };

// green for positive CAGR (stronger = closer to the best cell), red for losses
const heatColor = (cagr, maxCagr) => {
  if (cagr <= 0) return "rgba(239, 68, 68, 0.35)";
  const strength = maxCagr > 0 ? Math.min(cagr / maxCagr, 1) : 0;
  return `rgba(34, 197, 94, ${0.12 + strength * 0.5})`;
};

const PlateauHeatmap = ({ plateau }) => {
  const { takeProfitValues, stopLossValues, grid, barsLine, chosen } = plateau;
  const maxCagr = Math.max(...grid.map((g) => g.cagr), 0);
  const cell = (tp, sl) =>
    grid.find((g) => g.takeProfitPercent === tp && g.stopLossPercent === sl);
  const isChosen = (tp, sl) =>
    tp === chosen.takeProfitPercent && sl === chosen.stopLossPercent;

  return (
    <div>
      <h5 className="mb-2">
        <DashedTooltip text="CAGR for take profit (rows) x stop loss (columns) at -40%..+40% of your values. Your settings are outlined. A broad green area is a plateau (good); a lone bright cell is a lucky spike (overfit).">
          Parameter plateau (CAGR %)
        </DashedTooltip>
      </h5>
      <table>
        <thead>
          <tr>
            <th style={headStyle}>TP \ SL</th>
            {stopLossValues.map((sl) => (
              <th key={sl} style={headStyle}>
                {sl}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {takeProfitValues.map((tp) => (
            <tr key={tp}>
              <td style={{ ...headStyle, textAlign: "left" }}>{tp}</td>
              {stopLossValues.map((sl) => {
                const g = cell(tp, sl);
                return (
                  <td
                    key={sl}
                    title={`TP ${tp} / SL ${sl}: CAGR ${fmt(g.cagr, "%")}, DD ${fmt(g.maxDrawdown, "%")}, trades ${g.trades}`}
                    style={{
                      ...cellStyle,
                      textAlign: "center",
                      background: heatColor(g.cagr, maxCagr),
                      outline: isChosen(tp, sl) ? "2px solid #fff" : "none",
                      outlineOffset: -2,
                    }}
                  >
                    {Math.round(g.cagr)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      {barsLine.length > 0 && (
        <div className="mt-3 flex gap-3 flex-wrap items-center">
          <span style={{ opacity: 0.6 }}>Bars open exit:</span>
          {barsLine.map((b) => (
            <span
              key={b.daysOpenExitCount}
              style={{
                padding: "2px 8px",
                borderRadius: 4,
                background: heatColor(b.cagr, maxCagr),
                outline:
                  b.daysOpenExitCount === chosen.daysOpenExitCount
                    ? "2px solid #fff"
                    : "none",
              }}
            >
              {b.daysOpenExitCount}: {Math.round(b.cagr)}%
            </span>
          ))}
        </div>
      )}
    </div>
  );
};
PlateauHeatmap.propTypes = { plateau: PropTypes.object };

const BacktestHealthPanel = ({ health, loading, error }) => {
  const verdict = VERDICTS[health?.verdict];

  return (
    <GenericCard
      title="Backtest health"
      className="table-card"
      maxHeight={2000}
      tooltipText="Is this result robust or lucky? Re-runs the same strategy on the last 30% of the range, on each year, and with nearby take profit / stop loss values, then grades it. Daily exits."
    >
      {loading && !health && (
        <div className="py-6">
          <Spin /> <span className="ms-2">Running robustness checks...</span>
        </div>
      )}
      {error && !loading && <div className="red">{error}</div>}
      {health && verdict && (
        <div className="text-left" style={{ opacity: loading ? 0.5 : 1 }}>
          <div className="flex items-center gap-4 flex-wrap mb-4">
            <span
              style={{
                background: verdict.color,
                color: "#111",
                fontWeight: 700,
                padding: "4px 14px",
                borderRadius: 6,
                fontSize: "1.1rem",
              }}
            >
              {verdict.label}
            </span>
            <span style={{ opacity: 0.8 }}>{verdict.text}</span>
          </div>

          <div className="flex flex-col gap-1 mb-6">
            {health.checks.map((check) => (
              <div key={check.id} className="flex gap-2 items-baseline">
                {STATUS_ICONS[check.status]}
                <span style={{ minWidth: 150, fontWeight: 500 }}>
                  {check.label}
                </span>
                <span style={{ opacity: 0.8 }}>{check.detail}</span>
              </div>
            ))}
          </div>

          {health.holdout && (
            <div className="flex gap-10 flex-wrap items-start">
              <HoldoutTable holdout={health.holdout} />
              <YearsTable years={health.years} />
              <PlateauHeatmap plateau={health.plateau} />
            </div>
          )}
        </div>
      )}
    </GenericCard>
  );
};

BacktestHealthPanel.propTypes = {
  health: PropTypes.object,
  loading: PropTypes.bool,
  error: PropTypes.string,
};

export default BacktestHealthPanel;
